// src/components/navigation/user-menu.tsx
import { useState, useRef, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '@/components/providers/auth-provider'
import { userDropdownItems, hasAccess } from '@/lib/navigation'
import { cn, getInitials } from '@/lib/utils'
import { ChevronDown, LogOut } from 'lucide-react'
import type { User } from '@/types'

interface UserMenuProps {
  user: User | null
  className?: string
}

export function UserMenu({ user, className }: UserMenuProps) {
  const { logout } = useAuth()
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleSignOut = async () => {
    console.log('🔴 handleSignOut triggered')  // ✅ ADD
    try {
      console.log('🔴 About to call logout()')  // ✅ ADD
      await logout()
      console.log('🔴 Logout completed')  // ✅ ADD
    } catch (error) {
      console.error('🔴 Logout error:', error)
    }
  }
  if (!user) return null

  const userTier = user.subscription_tier || 'free'
  const displayName = user.full_name || user.email
  const initials = getInitials(displayName)

  return (
    <div className={cn('relative', className)} ref={dropdownRef}>
      {/* User Menu Trigger */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-2 px-3 py-2 rounded-lg hover:bg-muted transition-colors text-sm"
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        {/* User Avatar - UPDATED: Purple gradient */}
        <div className="w-7 h-7 bg-gradient-to-br from-digestr-500 to-digestr-600 rounded-full flex items-center justify-center text-white text-xs font-medium">
          {initials}
        </div>

        {/* User Name */}
        <span className="font-medium truncate max-w-32">
          {user.full_name || 'User'}
        </span>

        {/* Dropdown Arrow */}
        <ChevronDown className={cn('w-4 h-4 transition-transform', {
          'rotate-180': isOpen
        })} />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute right-0 top-full mt-2 w-64 bg-card border border-border rounded-lg shadow-lg z-50">
          <div className="py-2">
            {/* User Info Header - UPDATED: Purple gradient avatar */}
            <div className="px-3 py-2 border-b border-border">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-gradient-to-br from-digestr-500 to-digestr-600 rounded-full flex items-center justify-center text-white text-sm font-medium">
                  {initials}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{displayName}</p>
                  <p className="text-xs text-muted-foreground truncate">{user.email}</p>
                </div>
              </div>
            </div>

            {/* Menu Items */}
            <div className="py-1">
              {userDropdownItems.map((item) => {
                // Handle dividers
                if (item.divider) {
                  return (
                    <div key={item.key} className="border-t border-border my-1" />
                  )
                }

                // Handle sign out action - UPDATED: Better red styling
                if (item.key === 'sign-out') {
                  return (
                    <button
                      key={item.key}
                      onClick={handleSignOut}
                      className="w-full flex items-center space-x-3 px-3 py-2 text-sm text-left hover:bg-red-500/10 transition-colors text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                    >
                      <LogOut className="w-4 h-4" />
                      <span>{item.label}</span>
                    </button>
                  )
                }

                // Skip items that require higher tier
                if (item.requiredTier && !hasAccess({ requiredTier: item.requiredTier } as any, userTier)) {
                  return null
                }

                return (
                  <Link
                    key={item.key}
                    to={item.href!}
                    className="flex items-center space-x-3 px-3 py-2 text-sm hover:bg-muted transition-colors"
                    onClick={() => setIsOpen(false)}
                  >
                    {item.icon && <item.icon className="w-4 h-4" />}
                    <div className="flex-1">
                      <div>{item.label}</div>
                      {item.description && (
                        <div className="text-xs text-muted-foreground">{item.description}</div>
                      )}
                    </div>
                  </Link>
                )
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}